package com.milta.web

import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.milta.core.localization.LocalStrings
import com.milta.core.localization.RouteStrings
import com.milta.core.localization.WebDashboardStrings
import com.milta.feature.route.domain.entity.DriverRouteOverview
import com.milta.feature.route.domain.entity.GeocodeStatus
import com.milta.feature.route.domain.entity.FailureReason
import com.milta.feature.route.domain.entity.RouteStatus
import com.milta.feature.route.domain.usecase.GetRouteDetailUseCase
import com.milta.feature.route.domain.usecase.GetRouteProofsUseCase
import com.milta.feature.route.domain.usecase.GetTeamRoutesOverviewUseCase
import com.milta.feature.route.presentation.WebDashboardIntent
import com.milta.feature.route.presentation.WebDashboardState
import com.milta.feature.route.presentation.WebDashboardStopDisplay
import com.milta.feature.route.presentation.WebDashboardStopDisplayStatus
import com.milta.feature.route.presentation.WebDashboardTab
import com.milta.feature.route.presentation.WebDashboardViewModel
import org.jetbrains.compose.web.css.height
import org.jetbrains.compose.web.css.padding
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.width
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.Text
import org.koin.compose.koinInject
import kotlin.math.roundToInt

/**
 * Entry point wired from `Main.kt`'s "Tournées du jour" nav destination (MIL-48, DOM-06 —
 * Supervision) — owns the single [WebDashboardViewModel]. Snapshot-only, no realtime/polling (see
 * the ViewModel's own doc): the initial load happens in the ViewModel's `init` block, a manual
 * [WebDashboardIntent.Refresh] is the only way to reload afterwards. Mirrors the mobile
 * [com.milta.feature.route.presentation.GestionnaireDashboardScreen]'s KPI/route-card/stop-detail
 * wording and layout (see [WebDashboardStrings]'s doc), rebuilt in Compose HTML rather than shared —
 * that screen is Compose UI (canvas), incompatible with the DOM-based web dashboard.
 */
@Composable
fun WebDashboardContent() {
    val getTeamRoutesOverview: GetTeamRoutesOverviewUseCase = koinInject()
    val getRouteDetail: GetRouteDetailUseCase = koinInject()
    val getRouteProofs: GetRouteProofsUseCase = koinInject()
    val viewModel = remember { WebDashboardViewModel(getTeamRoutesOverview, getRouteDetail, getRouteProofs) }
    val state by viewModel.state.collectAsState()

    val strings = LocalStrings.current.route
    val commonStrings = LocalStrings.current.common
    val dashboardStrings = LocalStrings.current.webDashboard

    Div(
        attrs = { style { property("display", "flex"); property("flex-direction", "column"); property("gap", "16px") } },
    ) {
        TabAndRefreshRow(state = state, strings = strings, dashboardStrings = dashboardStrings, loadingLabel = commonStrings.loading, onIntent = viewModel::onIntent)

        val error = state.error
        if (error != null) {
            InlineBanner(kind = InlineBannerKind.Error, text = dashboardErrorMessage(error, strings, commonStrings.errorOffline))
        }

        if (state.selectedTab == WebDashboardTab.Today) {
            KpiRow(state = state, strings = strings, onIntent = viewModel::onIntent)
        }

        val overview = state.overview
        if (overview != null && overview.routes.isNotEmpty()) {
            FilterRow(state = state, dashboardStrings = dashboardStrings, onIntent = viewModel::onIntent)
        }

        when {
            state.isLoading && overview == null -> LoadingPlaceholder(commonStrings.loading)
            overview != null && overview.routes.isEmpty() -> EmptyRoutesCard(strings)
            overview != null && state.visibleRoutes.isEmpty() -> EmptyRoutesCard(strings)
            overview != null -> RoutesList(
                state = state,
                routes = state.visibleRoutes,
                strings = strings,
                dashboardStrings = dashboardStrings,
                offlineMessage = commonStrings.errorOffline,
                onIntent = viewModel::onIntent,
            )
        }
    }

    // Retour client 17/09/2026, point 12 — proof-photo dialog for a Delivered/Failed stop of the
    // expanded route. Rendered outside the scrollable Div above (same top-level-overlay pattern as
    // WebTeamScreen's RemoveDriverDialog) so its `position: fixed` isn't clipped by a scrolling ancestor.
    if (state.proofDialogDeliveryId != null) {
        val failedComment = state.expandedStops
            ?.firstOrNull { it.delivery.id == state.proofDialogDeliveryId }
            ?.takeIf { it.displayStatus == WebDashboardStopDisplayStatus.Failed }
            ?.let { state.proofComment }
        val failedReason = state.expandedStops
            ?.firstOrNull { it.delivery.id == state.proofDialogDeliveryId }
            ?.takeIf { it.displayStatus == WebDashboardStopDisplayStatus.Failed }
            ?.let { state.proofFailureReason }
        ProofPhotoDialog(state = state, failedReason = failedReason, failedComment = failedComment, strings = strings, onIntent = viewModel::onIntent)
    }
}

@Composable
private fun ProofPhotoDialog(
    state: WebDashboardState,
    failedReason: FailureReason?,
    failedComment: String?,
    strings: RouteStrings,
    onIntent: (WebDashboardIntent) -> Unit,
) {
    Div(
        attrs = {
            style {
                property("position", "fixed"); property("top", "0"); property("left", "0"); property("right", "0"); property("bottom", "0")
                property("background", "rgba(30,58,74,0.45)")
                property("display", "flex"); property("align-items", "center"); property("justify-content", "center")
                property("z-index", "1000")
            }
        },
    ) {
        Div(
            attrs = {
                style {
                    property("background", WebSurface)
                    property("border-radius", "14px")
                    width(360.px)
                    property("box-sizing", "border-box")
                    property("box-shadow", "0 12px 32px rgba(30,58,74,0.25)")
                    padding(22.px)
                }
            },
        ) {
            Div(
                attrs = { style { property("display", "flex"); property("align-items", "center"); property("justify-content", "space-between"); property("margin-bottom", "12px") } },
            ) {
                Div(attrs = { style { property("font-size", "16px"); property("font-weight", "700"); property("color", WebTextPrimary) } }) {
                    Text(strings.deliveryProofPhotoSectionLabel)
                }
                Div(
                    attrs = {
                        onClick { onIntent(WebDashboardIntent.DismissProof) }
                        style { property("cursor", "pointer"); property("color", WebTextSecondary); property("font-size", "18px"); property("line-height", "1") }
                    },
                ) { Text("×") }
            }

            Div(
                attrs = {
                    style {
                        width(100.percent); height(220.px)
                        property("border-radius", "10px"); property("overflow", "hidden")
                        property("background", WebSurfaceDim)
                        property("display", "flex"); property("align-items", "center"); property("justify-content", "center")
                    }
                },
            ) {
                var photoLoadFailed by remember(state.proofPhotoUrl) { mutableStateOf(false) }
                val photoUrl = state.proofPhotoUrl
                when {
                    state.isLoadingProof -> Div(attrs = { style { property("font-size", "12.5px"); property("color", WebTextSecondary) } }) { Text("…") }
                    photoUrl != null && !photoLoadFailed -> Img(
                        src = photoUrl,
                        alt = strings.deliveryProofPhotoSectionLabel,
                        attrs = {
                            style { width(100.percent); height(100.percent); property("object-fit", "cover") }
                            addEventListener("error") { photoLoadFailed = true }
                        },
                    )
                    // Retour 27/10 (point 3) — validated without photo, not a missing one.
                    else -> Div(
                        attrs = { style { property("font-size", "12.5px"); property("color", WebTextSecondary) } },
                    ) { Text(if (state.proofWithoutPhoto) strings.proofWithoutPhotoLabel else strings.proofPhotoUnavailableLabel) }
                }
            }

            if (failedReason != null || !failedComment.isNullOrBlank()) {
                Div(attrs = { style { property("margin-top", "14px"); property("font-size", "11px"); property("font-weight", "700"); property("letter-spacing", "0.04em"); property("text-transform", "uppercase"); property("color", WebTextSecondary) } }) {
                    Text(strings.deliveryProofCommentSectionLabel)
                }
                if (failedReason == FailureReason.RouteClosedByDriver) {
                    Div(attrs = { style { property("margin-top", "4px"); property("font-size", "13px"); property("font-weight", "600"); property("color", WebTextPrimary) } }) {
                        Text(strings.failureReasonRouteClosedByDriver)
                    }
                }
                if (!failedComment.isNullOrBlank()) {
                    Div(attrs = { style { property("margin-top", "4px"); property("font-size", "13px"); property("color", WebTextPrimary) } }) {
                        Text(failedComment)
                    }
                }
            }
        }
    }
}

@Composable
private fun TabAndRefreshRow(
    state: WebDashboardState,
    strings: RouteStrings,
    dashboardStrings: WebDashboardStrings,
    loadingLabel: String,
    onIntent: (WebDashboardIntent) -> Unit,
) {
    Div(
        attrs = { style { property("display", "flex"); property("align-items", "center"); property("justify-content", "space-between") } },
    ) {
        Div(attrs = { style { property("display", "flex"); property("gap", "8px") } }) {
            DashboardTabChip(label = strings.todayDateLabel, selected = state.selectedTab == WebDashboardTab.Today) {
                onIntent(WebDashboardIntent.SwitchTab(WebDashboardTab.Today))
            }
            DashboardTabChip(label = strings.periodTomorrowLabel, selected = state.selectedTab == WebDashboardTab.Tomorrow) {
                onIntent(WebDashboardIntent.SwitchTab(WebDashboardTab.Tomorrow))
            }
        }
        Button(
            attrs = {
                if (!state.isLoading) onClick { onIntent(WebDashboardIntent.Refresh) }
                style {
                    property("border", "1px solid $WebBorder"); property("border-radius", "8px")
                    property("background", WebSurface); property("color", WebTextPrimary)
                    property("font-size", "13px"); property("font-weight", "600")
                    property("padding", "9px 16px")
                    property("cursor", if (state.isLoading) "not-allowed" else "pointer")
                    property("opacity", if (state.isLoading) "0.6" else "1")
                }
                if (state.isLoading) attr("disabled", "true")
            },
        ) {
            // MIL-85 — "Actualiser" previously gave zero feedback once the first load had already
            // completed (state.isLoading was only ever checked for the very first render, gating
            // the full-screen LoadingPlaceholder). No animation here, just a disabled/dimmed state
            // and swapped label while the request is in flight.
            Text(if (state.isLoading) "⟳ $loadingLabel" else "⟳ ${dashboardStrings.refreshButtonLabel}")
        }
    }
}

@Composable
private fun DashboardTabChip(label: String, selected: Boolean, onClick: () -> Unit) {
    Div(
        attrs = {
            onClick { onClick() }
            style {
                property("background", if (selected) WebActiveTint else WebSurface)
                property("border", "1px solid " + (if (selected) WebTeal else WebBorder))
                property("border-radius", "50px")
                property("padding", "8px 16px"); property("cursor", "pointer")
                property("font-size", "13.5px"); property("font-weight", "700")
                property("color", if (selected) WebPrimary else WebTextSecondary)
            }
        },
    ) { Text(label) }
}

/** MIL-85 — client-side filter on the already-loaded [WebDashboardState.overview] (this screen
 * has no pagination, unlike "Historique des preuves"), never triggers a refetch. Status isn't
 * filtered here: the 3 status KPI cards ([KpiRow]) double as that filter directly — a separate
 * dropdown for the exact same 3 values would be redundant UI (explicit correction). */
@Composable
private fun FilterRow(
    state: WebDashboardState,
    dashboardStrings: WebDashboardStrings,
    onIntent: (WebDashboardIntent) -> Unit,
) {
    Div(
        attrs = { style { property("display", "flex"); property("gap", "10px"); property("flex-wrap", "wrap") } },
    ) {
        DriverFilterDropdown(
            drivers = state.availableDrivers,
            selectedDriverId = state.driverFilter,
            dashboardStrings = dashboardStrings,
            onSelect = { onIntent(WebDashboardIntent.SetDriverFilter(it)) },
        )
    }
}

@Composable
private fun DriverFilterDropdown(
    drivers: List<DriverRouteOverview>,
    selectedDriverId: String?,
    dashboardStrings: WebDashboardStrings,
    onSelect: (String?) -> Unit,
) {
    val options = listOf<Pair<String?, String>>(null to dashboardStrings.filterAllDriversLabel) +
        drivers.map { it.driverId to "${it.driverFirstName} ${it.driverLastName}" }
    WebNativeSelect(options = options, selectedValue = selectedDriverId, onSelect = onSelect)
}

/** MIL-85 — the 3 status KPI cards double as the status filter: clicking one applies
 * [WebDashboardIntent.SetStatusFilter] for that status, clicking the already-active one clears it
 * (toggle). The 4th card (deliveries count) isn't a status and stays inert. */
@Composable
private fun KpiRow(state: WebDashboardState, strings: RouteStrings, onIntent: (WebDashboardIntent) -> Unit) {
    val overview = state.overview
    val completed = overview?.completedDeliveries ?: 0
    val total = overview?.totalDeliveries ?: 0
    fun toggle(status: RouteStatus) {
        onIntent(WebDashboardIntent.SetStatusFilter(if (state.statusFilter == status) null else status))
    }
    Div(
        attrs = { style { property("display", "grid"); property("grid-template-columns", "repeat(4, 1fr)"); property("gap", "12px") } },
    ) {
        KpiCard(label = strings.routeStatusInProgress, value = state.inProgressRoutesCount.toString(), isActive = state.statusFilter == RouteStatus.InProgress, onClick = { toggle(RouteStatus.InProgress) })
        KpiCard(label = strings.routeStatusScheduled, value = state.scheduledRoutesCount.toString(), isActive = state.statusFilter == RouteStatus.Scheduled, onClick = { toggle(RouteStatus.Scheduled) })
        KpiCard(label = strings.routeStatusCompleted, value = state.completedRoutesCount.toString(), isActive = state.statusFilter == RouteStatus.Completed, onClick = { toggle(RouteStatus.Completed) })
        KpiCard(label = strings.statDeliveriesLabel, value = "$completed/$total ${strings.stopsCountLabel}")
    }
}

@Composable
private fun KpiCard(label: String, value: String, isActive: Boolean = false, onClick: (() -> Unit)? = null) {
    Div(
        attrs = {
            if (onClick != null) onClick { onClick() }
            style {
                property("background", if (isActive) WebActiveTint else WebSurface)
                property("border", "1px solid " + (if (isActive) WebTeal else WebBorder))
                property("border-radius", "12px"); padding(16.px)
                property("display", "flex"); property("flex-direction", "column"); property("align-items", "center")
                property("gap", "4px")
                if (onClick != null) property("cursor", "pointer")
            }
        },
    ) {
        Div(
            attrs = { style { property("font-size", "20px"); property("font-weight", "800"); property("color", if (isActive) WebPrimary else WebTextPrimary) } },
        ) { Text(value) }
        Div(
            attrs = {
                style {
                    property("font-size", "11px"); property("font-weight", "700")
                    property("letter-spacing", "0.04em"); property("text-transform", "uppercase")
                    property("color", if (isActive) WebPrimary else WebTextSecondary); property("text-align", "center")
                }
            },
        ) { Text(label) }
    }
}

@Composable
private fun RoutesList(
    state: WebDashboardState,
    routes: List<DriverRouteOverview>,
    strings: RouteStrings,
    dashboardStrings: WebDashboardStrings,
    offlineMessage: String,
    onIntent: (WebDashboardIntent) -> Unit,
) {
    val isToday = state.selectedTab == WebDashboardTab.Today
    Div(attrs = { style { property("display", "flex"); property("flex-direction", "column"); property("gap", "10px") } }) {
        routes.forEach { route ->
            val isExpanded = state.expandedRouteId == route.routeId
            RouteCard(
                route = route,
                isExpanded = isExpanded,
                isToday = isToday,
                progress = state.routeProgressByRouteId?.get(route.routeId),
                strings = strings,
                dashboardStrings = dashboardStrings,
                expandedStops = if (isExpanded) state.expandedStops else null,
                isLoadingDetail = isExpanded && state.isLoadingRouteDetail,
                detailError = if (isExpanded) state.routeDetailError else null,
                offlineMessage = offlineMessage,
                onClick = { if (isToday) onIntent(WebDashboardIntent.ExpandRoute(route.routeId)) },
                onIntent = onIntent,
            )
        }
    }
}

@Composable
private fun RouteCard(
    route: DriverRouteOverview,
    isExpanded: Boolean,
    isToday: Boolean,
    progress: Float?,
    strings: RouteStrings,
    dashboardStrings: WebDashboardStrings,
    expandedStops: List<WebDashboardStopDisplay>?,
    isLoadingDetail: Boolean,
    detailError: String?,
    offlineMessage: String,
    onClick: () -> Unit,
    onIntent: (WebDashboardIntent) -> Unit,
) {
    val driverName = "${route.driverFirstName} ${route.driverLastName}"
    Div(
        attrs = {
            style {
                property("background", WebSurface); property("border", "1px solid $WebBorder")
                property("border-radius", "12px"); property("overflow", "hidden")
            }
        },
    ) {
        Div(
            attrs = {
                if (isToday) onClick { onClick() }
                style {
                    padding(14.px, 16.px)
                    property("cursor", if (isToday) "pointer" else "default")
                    property("display", "flex"); property("flex-direction", "column"); property("gap", "10px")
                    property("box-sizing", "border-box")
                }
            },
        ) {
            Div(attrs = { style { property("display", "flex"); property("align-items", "center"); property("gap", "12px") } }) {
                RouteAvatar(driverName)
                Div(
                    attrs = { style { property("display", "flex"); property("flex-direction", "column"); property("flex", "1"); property("gap", "2px"); property("min-width", "0") } },
                ) {
                    Div(attrs = { style { property("font-size", "14px"); property("font-weight", "700"); property("color", WebTextPrimary) } }) { Text(driverName) }
                    val subtitle = routeSubtitle(route, isToday, strings)
                    if (subtitle != null) {
                        Div(attrs = { style { property("font-size", "12px"); property("color", WebTextSecondary) } }) { Text(subtitle) }
                    }
                }
                RouteStatusBadge(status = route.status, strings = strings)
                // MIL-85 — nothing previously signalled that a today's route card is clickable
                // (it already expanded in place on click, see RouteCard's onClick above). Hidden
                // entirely on Tomorrow, which never expands (onClick is a no-op there too).
                if (isToday) {
                    Div(
                        attrs = { style { property("font-size", "14px"); property("color", WebTextSecondary); property("line-height", "1") } },
                    ) { Text("${strings.routeCardDetailLabel} ${if (isExpanded) "▲" else "▼"}") }
                }
            }
            if (progress != null) {
                Div(attrs = { style { property("display", "flex"); property("align-items", "center"); property("gap", "10px") } }) {
                    ProgressBar(progress = progress)
                    Div(
                        attrs = { style { property("font-size", "12px"); property("font-weight", "700"); property("color", WebTextSecondary); property("white-space", "nowrap") } },
                    ) {
                        Text("${route.completedStopsCount}/${route.totalStopsCount} ${strings.stopsCountLabel} · ${(progress * 100).roundToInt()}%")
                    }
                }
            }
        }

        if (isExpanded) {
            ExpandedStopsSection(
                stops = expandedStops,
                isLoading = isLoadingDetail,
                error = detailError,
                strings = strings,
                dashboardStrings = dashboardStrings,
                offlineMessage = offlineMessage,
                onIntent = onIntent,
            )
        }
    }
}

@Composable
private fun ProgressBar(progress: Float) {
    Div(
        attrs = {
            style {
                property("flex", "1"); height(6.px); property("border-radius", "50px")
                property("background", WebSurfaceDim); property("overflow", "hidden")
            }
        },
    ) {
        Div(
            attrs = {
                style {
                    height(6.px)
                    width(progress.coerceIn(0f, 1f).times(100).percent)
                    property("border-radius", "50px")
                    property("background", "linear-gradient(120deg, $WebTeal, $WebMint)")
                }
            },
        ) {}
    }
}

@Composable
private fun RouteStatusBadge(status: RouteStatus, strings: RouteStrings) {
    // Same RouteStatus -> color mapping as the mobile app's canonical pill (DriverHomeScreen's
    // StatusPill / GestionnaireDashboardScreen's RouteStatusPill), in hex parity with Color.kt's
    // AccentLight/Teal and InProgressBg/InProgressInk tokens. Previously "Scheduled" was neutral
    // grey and "In progress" was WebActiveTint/WebPrimary (navy-on-pale-teal, the deprecated
    // BlueDeep primary) here only, a third distinct look for the same two statuses.
    val (bg, ink, label) = when (status) {
        RouteStatus.Scheduled -> Triple(WebAccentLight, WebTealAccent, strings.routeStatusScheduled)
        RouteStatus.InProgress -> Triple(WebInProgressBg, WebInProgressInk, strings.routeStatusInProgress)
        RouteStatus.Completed -> Triple(WebSuccessBg, WebSuccessInk, strings.routeStatusCompleted)
    }
    Div(
        attrs = {
            style {
                property("background", bg); property("color", ink)
                property("border-radius", "50px"); property("padding", "5px 12px")
                property("font-size", "11.5px"); property("font-weight", "700"); property("white-space", "nowrap")
            }
        },
    ) { Text(label) }
}

@Composable
private fun RouteAvatar(name: String) {
    val label = initials(name)
    Div(
        attrs = {
            style {
                width(38.px); height(38.px)
                property("border-radius", "50%")
                property("background", avatarColorFor(label))
                property("display", "flex"); property("align-items", "center"); property("justify-content", "center")
                property("flex-shrink", "0")
                property("color", "#FFFFFF"); property("font-weight", "700"); property("font-size", "13px")
            }
        },
    ) { Text(label) }
}

@Composable
private fun ExpandedStopsSection(
    stops: List<WebDashboardStopDisplay>?,
    isLoading: Boolean,
    error: String?,
    strings: RouteStrings,
    dashboardStrings: WebDashboardStrings,
    offlineMessage: String,
    onIntent: (WebDashboardIntent) -> Unit,
) {
    Div(
        attrs = {
            style {
                property("border-top", "1px solid $WebSurfaceDim")
                property("background", WebSurfaceDim)
                padding(14.px, 16.px)
                property("display", "flex"); property("flex-direction", "column"); property("gap", "8px")
                property("box-sizing", "border-box")
            }
        },
    ) {
        Div(
            attrs = {
                style {
                    property("font-size", "11px"); property("font-weight", "700"); property("letter-spacing", "0.05em")
                    property("text-transform", "uppercase"); property("color", WebTextSecondary)
                }
            },
        ) { Text(dashboardStrings.expandedStopsTitle) }

        when {
            error != null -> InlineBanner(kind = InlineBannerKind.Error, text = routeDetailErrorMessage(error, strings, offlineMessage))
            isLoading || stops == null -> Div(
                attrs = { style { property("font-size", "12.5px"); property("color", WebTextSecondary) } },
            ) { Text("…") }
            else -> stops.forEach { stop -> StopRow(stop = stop, strings = strings, dashboardStrings = dashboardStrings, onIntent = onIntent) }
        }
    }
}

private data class StopStatusStyle(val bg: String, val ink: String, val icon: String, val label: String)

@Composable
private fun StopRow(
    stop: WebDashboardStopDisplay,
    strings: RouteStrings,
    dashboardStrings: WebDashboardStrings,
    onIntent: (WebDashboardIntent) -> Unit,
) {
    val style = when (stop.displayStatus) {
        WebDashboardStopDisplayStatus.Delivered -> StopStatusStyle(WebSuccessBg, WebSuccessInk, "✓", strings.stopStatusDeliveredLabel)
        WebDashboardStopDisplayStatus.EnRoute -> StopStatusStyle(WebActiveTint, WebPrimary, "→", strings.stopStatusEnRouteLabel)
        WebDashboardStopDisplayStatus.Waiting -> StopStatusStyle(WebSurfaceDim, WebTextSecondary, "○", dashboardStrings.stopStatusWaitingLabel)
        WebDashboardStopDisplayStatus.Failed -> StopStatusStyle(WebErrorBg, WebErrorInk, "✗", dashboardStrings.stopStatusFailedLabel)
        WebDashboardStopDisplayStatus.Untreated -> StopStatusStyle(WebWarningBg, WebWarning, "⊘", strings.stopStatusUntreatedLabel)
    }
    // Retour client 17/09/2026, point 12 — only a finished stop (Delivered/Failed) has a proof photo
    // to show; Waiting/EnRoute/Untreated rows stay inert, same as before this ticket.
    val isFinished = stop.displayStatus == WebDashboardStopDisplayStatus.Delivered || stop.displayStatus == WebDashboardStopDisplayStatus.Failed
    Div(
        attrs = {
            if (isFinished) onClick { onIntent(WebDashboardIntent.ShowProof(stop.delivery.id)) }
            style {
                property("display", "flex"); property("align-items", "center"); property("gap", "10px")
                property("background", WebSurface); property("border-radius", "8px")
                padding(10.px, 12.px); property("box-sizing", "border-box")
                if (isFinished) property("cursor", "pointer")
            }
        },
    ) {
        Div(
            attrs = {
                style {
                    property("background", style.bg); property("color", style.ink)
                    property("border-radius", "50px"); property("padding", "3px 9px")
                    property("font-size", "11px"); property("font-weight", "700"); property("white-space", "nowrap")
                }
            },
        ) { Text("${style.icon} ${style.label}") }
        Div(attrs = { style { property("display", "flex"); property("flex-direction", "column"); property("flex", "1"); property("gap", "1px"); property("min-width", "0") } }) {
            Div(attrs = { style { property("font-size", "13px"); property("font-weight", "600"); property("color", WebTextPrimary) } }) {
                Text(stop.delivery.recipientName.ifBlank { strings.unknownRecipientLabel })
            }
            Div(attrs = { style { property("font-size", "12px"); property("color", WebTextSecondary) } }) { Text(stop.delivery.address) }
            // MIL-91 — HERE validation status of the stop's address (same badge texts as the import screens).
            val addressVerified = stop.delivery.geocodeStatus == GeocodeStatus.Geocoded
            Div(attrs = { style { property("font-size", "11px"); property("font-weight", "700"); property("color", if (addressVerified) WebSuccessInk else WebWarning) } }) {
                Text(if (addressVerified) LocalStrings.current.common.addressVerifiedBadge else LocalStrings.current.common.addressNeedsReviewBadge)
            }
            // Retour client — même les deux champs déjà affichés côté chauffeur (deadlineBeforePrefix/
            // packagesCountLabel, voir RouteInProgressScreen.kt), jamais vus côté Gestionnaire avant.
            Div(attrs = { style { property("font-size", "11px"); property("color", WebTextSecondary) } }) {
                val deadline = stop.delivery.deliveryTimeLimit
                val parts = buildList {
                    if (deadline != null) add("${strings.deadlineBeforePrefix} ${deadline.substringAfter('T', "").take(5)}")
                    add("${stop.delivery.packageCount.coerceAtLeast(1)} ${strings.packagesCountLabel}")
                }
                Text(parts.joinToString(" · "))
            }
        }
    }
}

@Composable
private fun EmptyRoutesCard(strings: RouteStrings) {
    Div(
        attrs = {
            style {
                property("background", WebSurface); property("border", "1px solid $WebBorder")
                property("border-radius", "12px"); padding(40.px, 24.px)
                property("text-align", "center"); property("box-sizing", "border-box")
            }
        },
    ) {
        Div(
            attrs = { style { property("font-size", "15px"); property("font-weight", "700"); property("color", WebTextPrimary); property("margin-bottom", "6px") } },
        ) { Text(strings.emptyTeamRoutesTitle) }
        Div(attrs = { style { property("font-size", "13px"); property("color", WebTextSecondary) } }) { Text(strings.emptyTeamRoutesSubtitle) }
    }
}

@Composable
private fun LoadingPlaceholder(label: String) {
    Div(
        attrs = { style { property("padding", "40px"); property("text-align", "center"); property("color", WebTextSecondary); property("font-size", "13px") } },
    ) { Text(label) }
}

/** "2026-08-06T08:12:00+00:00" -> "08:12" — mirrors [com.milta.feature.route.presentation.GestionnaireDashboardScreen]'s
 * private `formatTime`. */
private fun formatTime(iso: String): String {
    val timePart = iso.substringAfter('T', missingDelimiterValue = "")
    return if (timePart.length >= 5) timePart.take(5) else iso
}

/**
 * "Démarré à 08:12" / "Terminé à 12:47" / "Non démarrée" on [WebDashboardTab.Today] (mirrors the
 * mobile Gestionnaire dashboard's own card subtitle exactly). On [WebDashboardTab.Tomorrow], the
 * ticket only asks for "l'heure si déjà dispo" — a not-yet-started future route (the normal case)
 * shows no subtitle at all rather than a "Non démarrée" that would just be noise for a route that
 * hasn't happened yet.
 */
private fun routeSubtitle(route: DriverRouteOverview, isToday: Boolean, strings: RouteStrings): String? {
    val completedAt = route.completedAt
    val startedAt = route.startedAt
    return when {
        // Cross-module smart cast isn't allowed on completedAt/startedAt (declared in :shared),
        // hence the local val bindings above.
        isToday && route.status == RouteStatus.Completed && completedAt != null ->
            "${strings.routeCompletedAtPrefix} ${formatTime(completedAt)}"
        startedAt != null -> "${strings.routeStartedAtPrefix} ${formatTime(startedAt)}"
        isToday -> strings.routeNotStartedLabel
        else -> null
    }
}

/** Mirrors [WebDashboardViewModel.mapError]'s codes exactly. */
private fun dashboardErrorMessage(code: String, strings: RouteStrings, offlineMessage: String): String = when (code) {
    "invalid_date" -> strings.errorInvalidDate
    "offline" -> offlineMessage
    else -> strings.errorGeneric
}

/** Mirrors [WebDashboardViewModel.mapRouteDetailError]'s codes exactly. */
private fun routeDetailErrorMessage(code: String, strings: RouteStrings, offlineMessage: String): String = when (code) {
    "route_not_found" -> strings.errorRouteNotFound
    "forbidden" -> strings.errorForbidden
    "offline" -> offlineMessage
    else -> strings.errorGeneric
}
